import { useEffect, useRef, useState } from 'react';
import * as Ably from 'ably';

import { getAblyClient } from 'core/services/ably';

/**
 * Subscribes to a single event on an Ably channel for as long as the component is mounted.
 * The channel name must include its prefix (`public:` or `private:`), the hook uses it as is.
 * Pass a null channel name to skip subscribing (for example while an id is still missing).
 * The handler is read from a ref, so passing an inline function does not resubscribe on every render.
 */
const useAblyChannel = <T = any>(channelName: string | null, event: string, onMessage: (data: T) => void) => {
	const [connectionState, setConnectionState] = useState<Ably.ConnectionState>('initialized');

	const handlerRef = useRef(onMessage);
	handlerRef.current = onMessage;

	useEffect(() => {
		if (!channelName) return;

		const client = getAblyClient();
		if (!client) return;

		const fullChannelName = channelName;

		const channel = client.channels.get(fullChannelName);
		const listener = (message: Ably.Message) => handlerRef.current(message.data as T);

		channel.subscribe(event, listener);

		setConnectionState(client.connection.state);
		const connectionListener = (stateChange: Ably.ConnectionStateChange) => setConnectionState(stateChange.current);
		client.connection.on(connectionListener);

		// in development, trace every message and state change so a wrong event name or payload shape is visible
		const isDev = process.env['REACT_APP_ENV'] === 'development';
		const traceListener = (message: Ably.Message) =>
			console.log(
				`[ably] ${fullChannelName} received "${message.name}"`,
				message.name === event ? message.data : `(ignored, waiting for "${event}")`,
				message.data,
			);

		// attached means messages will arrive, failed usually means the key or token lacks access to the channel
		const channelStateListener = (stateChange: Ably.ChannelStateChange) =>
			console.log(`[ably] ${fullChannelName} is ${stateChange.current}`, stateChange.reason ?? '');

		if (isDev) {
			console.log(`[ably] subscribing to "${event}" on ${fullChannelName}, connection is ${client.connection.state}`);
			channel.subscribe(traceListener);
			channel.on(channelStateListener);
		}

		return () => {
			channel.unsubscribe(event, listener);
			if (isDev) {
				channel.unsubscribe(traceListener);
				channel.off(channelStateListener);
			}
			client.channels.release(fullChannelName);
			client.connection.off(connectionListener);
		};
	}, [channelName, event]);

	return connectionState;
};

export default useAblyChannel;
